import { effect, inject, Injectable, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ICON_PATH } from '@app/cnst/icons.cnst'
import { decorate, ErrorHandlerType } from '@app/decorators/decorators'
import { api } from '@app/srv/api.service'
import { dispatch, getState, select } from '@app/srv/store.service'
import { tr } from '@app/srv/translation.util'
import { localTime } from '@naturalcycles/js-lib'
import {
  AccountTM,
  Achievement,
  ACHIEVEMENT_BY_QUIZ,
  ActivationChannel,
  Goal,
  HardwareId,
  MediaType,
  Mens,
  UserAchievementsProgress,
} from '@naturalcycles/shared'
import { ConfettiModal, ConfettiModalProps } from '../modals/confetti/confetti.modal'
import { MessageModal } from '../modals/message/message.modal'
import { Message } from '../model/message.model'
import { UFEntry } from '../model/uf.model'
import { GraphSource } from '../pages/graph/srv/graph.cnst'
import { GraphService } from '../pages/graph/srv/graph.service'
import { EventService } from './event.service'
import { MarkdownService } from './markdown.service'
import { Priority } from './popup.cnst'
import { PopupController } from './popup.controller'
import { UFService } from './uf.service'

interface AchievementModel {
  title: string
  text: string
  icon: string
  state: string
  disabled: string
  key: string
}

const ACHIEVEMENTS_PREVENT: Achievement[] = [
  Achievement.signupAndInstallTheApp,
  Achievement.completedAppTour,
  Achievement.verifiedEmail,
  Achievement.calibratedThermometer,
  Achievement.firstTemperatureMeasurement,
  Achievement.firstDisabledMeasurement,
  Achievement.upgradedAccount,
  Achievement.fiveInAWeek,
  Achievement.twentyInAMonth,
  Achievement.lhRecommendation,
  Achievement.lhResult,
  Achievement.ovulation,
  Achievement.referredFriend,
  Achievement.sexPrevent,
  Achievement.greenDays40,
  Achievement.greenDays50,
  Achievement.greenDays60,
  Achievement.greenDays70,
  Achievement.cycles10,
  Achievement.cycles50,
  Achievement.cycles100,
  Achievement.year1,
  Achievement.year10,
  Achievement.helpedNCResearch,
  Achievement.reportedABug,
]

const ACHIEVEMENTS_PLAN: Achievement[] = [
  Achievement.signupAndInstallTheApp,
  Achievement.completedAppTour,
  Achievement.verifiedEmail,
  Achievement.calibratedThermometer,
  Achievement.firstTemperatureMeasurement,
  Achievement.firstDisabledMeasurement,
  Achievement.upgradedAccount,
  Achievement.fiveInAWeek,
  Achievement.twentyInAMonth,
  Achievement.lhRecommendation,
  Achievement.lhResult,
  Achievement.ovulation,
  Achievement.referredFriend,
  Achievement.sexPlan,
  Achievement.pregRecommendation,
  Achievement.cycles10,
  Achievement.cycles50,
  Achievement.cycles100,
  Achievement.year1,
  Achievement.year10,
  Achievement.helpedNCResearch,
  Achievement.reportedABug,
]

const ACHIEVEMENTS_PREGNANT: Achievement[] = [
  Achievement.signupAndInstallTheApp,
  Achievement.verifiedEmail,
  Achievement.calibratedThermometer,
  Achievement.firstTemperatureMeasurement,
  Achievement.firstDisabledMeasurement,
  Achievement.upgradedAccount,
  Achievement.fiveInAWeek,
  Achievement.twentyInAMonth,
  Achievement.referredFriend,
  Achievement.year1,
  Achievement.year10,
  Achievement.helpedNCResearch,
  Achievement.reportedABug,
]

const ACHIEVEMENTS_EXCLUDED_FOR_OURA: Achievement[] = [
  Achievement.calibratedThermometer,
  Achievement.readGuideMeasure,
]

const ACHIEVEMENTS_BY_GOAL: Record<Goal, Achievement[]> = {
  [Goal.PREVENT]: ACHIEVEMENTS_PREVENT,
  [Goal.PLAN]: ACHIEVEMENTS_PLAN,
  [Goal.PREGNANT]: ACHIEVEMENTS_PREGNANT,
  [Goal.RECOVERY]: ACHIEVEMENTS_PLAN,
  // DEV-17462 TODO: Update with correct achievements
  [Goal.POSTPARTUM]: ACHIEVEMENTS_PLAN,
  // TODO-PMP: Revisit for Perimenopause
  [Goal.PERIMENOPAUSE]: ACHIEVEMENTS_PREVENT,
}

@Injectable({ providedIn: 'root' })
export class AchievementsService {
  constructor() {
    // Popup Achievements
    effect(() => {
      const enablePopups = this.enablePopups()
      const achievement = this.newAchievement()
      const today = this.todayEntry()

      if (!enablePopups || !achievement || !today) return
      if (today.goal === Goal.RECOVERY) return // no achievements in recovery mode please

      if (achievement === Achievement.ovulation) {
        void this.showOvulationAchievement(today)
        return
      }

      void this.popupAchievement(achievement as Achievement)
    })

    // Trigger quiz achievement
    effect(() => {
      const quizId = this.eventService.quizOpened()
      const achievement = ACHIEVEMENT_BY_QUIZ[quizId!]

      if (!quizId || !achievement) return

      void this.completeAchievement(achievement)
    })
  }

  public enablePopups = signal(false)

  private ufService = inject(UFService)
  private graphService = inject(GraphService)
  private markdownService = inject(MarkdownService)
  private eventService = inject(EventService)
  private popupController = inject(PopupController)

  private newAchievement = toSignal(select(s => s.achievements.newAchievement))
  private todayEntry = toSignal(this.ufService.todayEntry$)

  public getAchievementsList(account: AccountTM): AchievementModel[] {
    const { goal, hwId, activationChannel, achievements: userAchievements } = account
    const progress = account.achievementsProgress || {}
    const achievements: AchievementModel[] = []

    // changing order here will change order they are displayed
    // needs to have different list for plan / prevent (type)
    let allAchievements = this.getAllAchievements()

    // remove calibration for clearblue T1 users and other HwId's than T1/T2
    if (
      (activationChannel === ActivationChannel.CLEARBLUE && hwId === HardwareId.ORAL_THERMOMETER) ||
      !(hwId === HardwareId.ORAL_THERMOMETER || hwId === HardwareId.UEBE_THERMOMETER)
    ) {
      allAchievements = allAchievements.filter(ach => ach !== Achievement.calibratedThermometer)
    }

    if (hwId === HardwareId.OURA) {
      allAchievements = allAchievements.filter(ach => !ACHIEVEMENTS_EXCLUDED_FOR_OURA.includes(ach))
    }

    const goalAchievements = ACHIEVEMENTS_BY_GOAL[goal!]

    for (const ach of allAchievements) {
      const achievement: AchievementModel = {} as AchievementModel
      achievement.key = ach
      achievement.text = this.getAchievementText(ach)
      achievement.icon = ICON_PATH + 'achievement-' + ach + '.svg'
      achievement.disabled = this.getAchievementDisabled(ach)
      achievement.state = this.getAchievementState(ach, userAchievements)
      achievement.title =
        this.getAchievementTitle(ach) +
        this.getAchievementProgress(ach, achievement.state, progress)

      if (goalAchievements.includes(ach) || achievement.state === 'achieved') {
        achievements.push(achievement)
      }
    }
    return achievements
  }

  public getAchievementProgress(
    achievementKey: string,
    achieved: string,
    progress: UserAchievementsProgress,
  ): string {
    let title = ''

    if (achievementKey === 'fiveInAWeek' && achieved === 'unachieved' && progress?.fiveInAWeek) {
      title += ' (' + progress.fiveInAWeek + '/5)'
    }

    if (
      achievementKey === 'twentyInAMonth' &&
      achieved === 'unachieved' &&
      progress?.twentyInAMonth
    ) {
      title += ' (' + progress.twentyInAMonth + '/20)'
    }

    return title
  }

  private getAchievementTitle(achievementKey: string): string {
    return tr(`achievement--${achievementKey}--title`)
  }

  private getAchievementText(achievementKey: string): string {
    return tr(`achievement--${achievementKey}`)
  }

  private getAchievementDisabled(achievementKey: Achievement): string {
    const enabled = [
      Achievement.verifiedEmail,
      Achievement.completedAppTour,
      Achievement.referredFriend,
      Achievement.signupAndInstallTheApp,
      Achievement.firstTemperatureMeasurement,
      Achievement.firstDisabledMeasurement,
      Achievement.upgradedAccount,
      Achievement.calibratedThermometer,
    ]

    return enabled.includes(achievementKey) ? '' : 'disabled'
  }

  public getAchievementState(achievement: Achievement, userAchievements: Achievement[]): string {
    return userAchievements.includes(achievement) ? 'achieved' : 'unachieved'
  }

  private getAllAchievements(): Achievement[] {
    return [
      Achievement.signupAndInstallTheApp,
      Achievement.completedAppTour,
      Achievement.verifiedEmail,
      Achievement.calibratedThermometer,
      Achievement.firstTemperatureMeasurement,
      Achievement.firstDisabledMeasurement,
      Achievement.upgradedAccount,
      Achievement.fiveInAWeek,
      Achievement.twentyInAMonth,
      Achievement.lhRecommendation,
      Achievement.lhResult,
      Achievement.ovulation,
      Achievement.referredFriend,
      Achievement.sexPrevent,
      Achievement.sexPlan,
      Achievement.pregRecommendation,
      Achievement.greenDays40,
      Achievement.greenDays50,
      Achievement.greenDays60,
      Achievement.greenDays70,
      Achievement.cycles10,
      Achievement.cycles50,
      Achievement.cycles100,
      Achievement.year1,
      Achievement.year10,
      Achievement.helpedNCResearch,
      Achievement.reportedABug,
    ]
  }

  @decorate({
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async completeAchievement(achievement: Achievement): Promise<void> {
    const { account } = getState()
    if (account.achievements.includes(achievement)) return

    await api.post(`achievements/complete/${achievement}`)
  }

  private async showOvulationAchievement(entry: UFEntry): Promise<void> {
    const { goal, mens } = entry

    const confettiProps: ConfettiModalProps = {
      title: 'achievement--ovulation--title',
      body: 'achievement--ovulation',
      image: './assets/img/achievements/green-day.svg',
      ctas: [
        {
          title: 'txt-go-to-graph',
          uid: 'achievementOvulation__openGraph__button',
        },
      ],
    }

    if (goal === Goal.PLAN || mens === Mens.MENSTRUATION) {
      confettiProps.title = 'achievement--ovulation--plan--title'
      confettiProps.image = './assets/img/achievements/green-day-plan.svg'
    }

    const popup = await this.popupController.presentModal(
      {
        component: ConfettiModal,
        componentProps: {
          ...confettiProps,
          analyticsProps: { id: 'OvulationAchievement' },
        },
      },
      'modal-achievementOvulation',
      Priority.NONE,
      2000,
    )

    const { data } = (await popup.onWillDismiss()) || {}

    if (data === 'achievementOvulation__openGraph__button') {
      void this.graphService.openGraph(GraphSource.OVULATION_ACHIEVEMENT)
    }

    dispatch('extendAchievements', { newAchievement: undefined })
  }

  private async popupAchievement(achievement: Achievement): Promise<void> {
    const created = localTime.nowUnix()
    let msgKey = `achievement--${achievement}--msg`
    let id = `achievement-${achievement}-msg_${created}`

    if (achievement === Achievement.lhRecommendation) {
      const { account } = getState()
      const goal = Goal[account.goal!].toLowerCase()
      id = `achievement-${achievement}-${goal}-msg_${created}`
      msgKey =
        goal === Goal[Goal.PLAN]
          ? `achievement--${achievement}-${goal}--msg`
          : `achievement--${achievement}--msg`
    }

    const message: Message = {
      msgKey: achievement,
      id,
      created,
      msg: this.markdownService.parse(tr(msgKey)),
      media: [
        {
          type: MediaType.ICON,
          url: achievement,
        },
      ],
    }

    const popup = await this.popupController.presentModal(
      {
        component: MessageModal,
        componentProps: {
          message,
          analyticsProps: { Message_key: message.msgKey }, // eslint-disable-line @typescript-eslint/naming-convention
        },
      },
      `modal-message-${message.msgKey}`,
      Priority.NONE,
      2000,
    )

    await popup.onWillDismiss()

    dispatch('extendAchievements', { newAchievement: undefined })
  }
}
